import {
  selectLocaleImprovementsEnabled,
  selectOtherProjects,
  UserContextRootState,
} from 'core/userContext';
import { isEmpty } from 'lodash';
import { Locale } from 'modules/domains/template';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';

import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { SendSMS } from 'ui/icons';
import {
  Modal,
  MODAL_SIZES,
  MODAL_VARIANTS,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
} from 'ui/modal';
import { BaseOption } from 'ui/selects';
import { spacing } from 'ui/theme';

import { useQueryTemplateAndProjectLocales } from 'modules/campaigns/lib/hooks/useQueryTemplateAndProjectLocales';
import { FolderId } from 'modules/domains/foldering';
import { DefaultLocaleSelectFormField } from 'modules/templateEditor/common/DefaultLocaleSelectFormField';

import { ImageWrapper, LabelledSelect, PreviewImg, TemplateNameInput } from '../../components';
import { SEGMENT_TRACKED_TEMPLATE_ACTION_TRIGGERS } from '../../constants';
import { copyTemplate, getMessageTypesAndSenders } from '../../ducks/actions';
import {
  selectIsFetchingMessageTypesAndSenders,
  selectMessageTypesAndSenders,
} from '../../ducks/selectors';
import {
  IndexPageTemplate,
  MessageTypesAndSenders,
  TemplateIndexRootState,
  TemplateWithMessageMediumLabels,
} from '../../types';

import {
  COPY_MODAL_DISPLAY_CONTENT,
  COPY_MODAL_TEST_ID,
  RADIO_OPTIONS,
} from './CopyTemplateModal.constants';
import {
  Body2Label,
  Body2Span,
  Name,
  PreviewContainer,
  RadioContainer,
  RadioOption,
  Spacer,
  StyledRadio,
} from './CopyTemplateModal.styled';
import {
  getMessageMediumSlug,
  mapEmailSendersToOptions,
  mapLocalesToTag,
  mapMessageTypesToOptions,
  mapSMSSendersToOptions,
  removeDefaultLocale,
} from './CopyTemplateModal.utils';
import { LocaleMultiSelect } from './LocaleMultiSelect';

const StyledName = styled(Name)`
  display: inline-block;
  margin-left: ${spacing.half};
`;

type CopyTemplateModalProps = {
  closeModal: () => void;
  currentFolderId?: FolderId;
  currentCampaignId?: number;
  onCopyTemplateClick?: () => void;
  refetch: () => void;
  /** object with selected template info */
  template: TemplateWithMessageMediumLabels | IndexPageTemplate;
  /** String that describes what triggered the copy dialog to appear. Used for Segment tracking */
  triggeredBy: SEGMENT_TRACKED_TEMPLATE_ACTION_TRIGGERS;
} & Partial<ModalProps>;

export const CopyTemplateModal: FunctionComponent<
  React.PropsWithChildren<CopyTemplateModalProps>
> = ({
  closeModal = () => null,
  currentFolderId = null,
  currentCampaignId = null,
  onCopyTemplateClick,
  refetch = () => null,
  template,
  triggeredBy,
  ...props
}) => {
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const templateId = template.id;
  const { isTemplateDefaultLocaleModifiable } = useLocaleSettingsContext();
  // IMPORTANT: the query is used directly here because the copy modal appears on the index page
  // (where the context would not be populated) as well as the edit page
  const { templateAndProjectLocales } = useQueryTemplateAndProjectLocales({
    skip: !isLocaleImprovementsEnabled,
    variables: { templateId },
  });
  const defaultTemplateName = template?.name ? `Copy of ${template.name}` : '';
  // Fields used to copy template stored in state
  const [templateName, setTemplateName] = useState(defaultTemplateName);
  // radio option of copying to current or another project, defaults to neither being selected
  const [checkedRadio, setCheckedRadio] = useState(RADIO_OPTIONS.CURRENT_PROJECT);
  const [destProjectId, setDestProjectId] = useState<number>();
  const [destMessageTypeId, setDestMessageTypeId] = useState<number>();
  const [destSmsSenderId, setDestSmsSender] = useState<number>();
  const [destEmailSender, setDestEmailSender] = useState<string>();
  const [defaultLocale, setDefaultLocale] = useState<Locale>({} as Locale);
  // Make default same as LocaleMultiSelect
  const [copiedLocales, setCopiedLocales] = useState<string[]>(
    mapLocalesToTag(templateAndProjectLocales.activeTemplateLocales),
  );

  const [{ messageTypes, emailSenders, smsSenders }, setMessageTypesAndSenders] =
    useState<MessageTypesAndSenders>({
      messageTypes: undefined,
      emailSenders: undefined,
      smsSenders: undefined,
    });

  // set up variables for easy use later
  const { messageMedium, id: srcTemplateId, previewUrl, name: srcName } = template;
  const isMediumEmail = messageMedium === 'Email';
  const isMediumSMS = messageMedium === 'SMS';
  const isAnotherProjectSelected = checkedRadio === RADIO_OPTIONS.ANOTHER_PROJECT;

  // parses messageMedium into the slug needed to fetch message types and senders
  const messageMediumSlug = getMessageMediumSlug(messageMedium);

  // focuses name on open
  useEffect(() => document?.getElementById('copy-template-name-input')?.focus(), []);

  // SELECTORS
  // selectors used to fetch data for copy template to another project and then maps to select options
  const projects = useSelector<UserContextRootState, BaseOption<number>[]>((state) =>
    selectOtherProjects(state).map(({ id, name }) => ({ label: name, id: `${id}`, value: id })),
  );

  const destProjectName: string | undefined = useMemo(() => {
    const destProject = projects.find((project) => project.value === destProjectId);
    return destProject?.label as string;
  }, [destProjectId, projects]);

  // the projects message types and data, returns null if it doesnt exist
  const projectMessageTypesAndSenders = useSelector<TemplateIndexRootState, MessageTypesAndSenders>(
    (state) =>
      selectMessageTypesAndSenders(state, {
        projectId: destProjectId,
        messageMedium: messageMediumSlug,
      }),
  );

  const isFetchingMessageTypesAndSenders = useSelector<TemplateIndexRootState, boolean>((state) =>
    selectIsFetchingMessageTypesAndSenders(state),
  );

  const handleCopiedLocales = (vals: string[]) => {
    setCopiedLocales(vals);
  };

  const handleDefaultLocale = (val: Locale) => {
    setDefaultLocale(val);
  };

  const dispatch = useDispatch();
  // Fetch message types and senders if new destination project is selected
  useEffect(() => {
    // dont fetch if data has already been fetched
    if (destProjectId && !projectMessageTypesAndSenders) {
      dispatch(
        getMessageTypesAndSenders({
          projectId: destProjectId,
          messageMedium: messageMediumSlug,
        }),
      );
    } else if (destProjectId && projectMessageTypesAndSenders) {
      setMessageTypesAndSenders(projectMessageTypesAndSenders);
    }
  }, [destProjectId]);

  // sets messageTypes and senders when loading finishes
  useEffect(() => {
    if (!isFetchingMessageTypesAndSenders && projectMessageTypesAndSenders)
      setMessageTypesAndSenders(projectMessageTypesAndSenders);
  }, [isFetchingMessageTypesAndSenders]);

  // sets selected locales to copy when loading finishes
  useEffect(() => {
    const { activeTemplateLocales, templateDefaultLocale } = templateAndProjectLocales;
    if (activeTemplateLocales?.length && !copiedLocales?.length) {
      setCopiedLocales(mapLocalesToTag(activeTemplateLocales));
    }
    if (templateDefaultLocale && isEmpty(defaultLocale)) {
      setDefaultLocale(templateDefaultLocale);
    }
  }, [templateAndProjectLocales]);

  // boolean for disabling copy button
  const shouldDisableCopyButton =
    // if template name is null or where to copy project is not selected disable button
    templateName.length === 0 ||
    templateName.length > 255 ||
    checkedRadio === RADIO_OPTIONS.UNSELECTED ||
    // if the current project is checked this is just false, if another project is selected it goes through the logic
    (checkedRadio !== RADIO_OPTIONS.CURRENT_PROJECT &&
      // if destProject id or dest messagetype id is not selected disable button
      (!destProjectId ||
        !destMessageTypeId ||
        // if template medium is email or sms and the respective sender is not selected disable button
        (isMediumEmail && !destEmailSender) ||
        (isMediumSMS && !destSmsSenderId)));

  // This is more explicit because TS can't infer typings correctly when it's combined as one component
  let senderSelect = null;
  if (isMediumEmail) {
    senderSelect = (
      <>
        <Spacer />
        <LabelledSelect
          title={COPY_MODAL_DISPLAY_CONTENT.selects.sender.title}
          id="sender-select"
          data-test="sender-select"
          placeholder={COPY_MODAL_DISPLAY_CONTENT.selects.sender.placeholder}
          empty={COPY_MODAL_DISPLAY_CONTENT.selects.sender.empty}
          options={mapEmailSendersToOptions(emailSenders)}
          onSelect={setDestEmailSender}
        />
      </>
    );
  } else if (isMediumSMS) {
    senderSelect = (
      <>
        <Spacer />
        <LabelledSelect
          title={COPY_MODAL_DISPLAY_CONTENT.selects.sender.title}
          id="sender-select"
          data-test="sender-select"
          placeholder={COPY_MODAL_DISPLAY_CONTENT.selects.sender.placeholder}
          empty={COPY_MODAL_DISPLAY_CONTENT.selects.sender.empty}
          options={mapSMSSendersToOptions(smsSenders)}
          onSelect={setDestSmsSender}
        />
      </>
    );
  }

  return (
    <Modal
      withPortal
      id={COPY_MODAL_TEST_ID}
      isModalOpen={true}
      onClose={closeModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
      data-test={`copy-template-modal`}
      {...props}
    >
      <ModalHeader>{COPY_MODAL_DISPLAY_CONTENT.modalHeader}</ModalHeader>

      <ModalBody id="copy-template-modal-body">
        {/* Preview Image */}
        <PreviewContainer>
          <ImageWrapper>
            {previewUrl ? <PreviewImg alt="preview" src={previewUrl} /> : <SendSMS />}
          </ImageWrapper>
          <StyledName>{srcName}</StyledName>
        </PreviewContainer>
        <Spacer />

        {/* Select a radio button to copy to this project or another project */}
        <Body2Label>{COPY_MODAL_DISPLAY_CONTENT.radio.header}</Body2Label>
        <RadioContainer>
          <RadioOption>
            <StyledRadio
              data-test="current-proj-radio"
              id="current-proj-radio"
              isChecked={checkedRadio === RADIO_OPTIONS.CURRENT_PROJECT}
              name="current-proj-radio"
              value={RADIO_OPTIONS.CURRENT_PROJECT}
              onClick={() => setCheckedRadio(RADIO_OPTIONS.CURRENT_PROJECT)}
            />
            <Body2Span
              as="label"
              htmlFor="current-proj-radio"
              onClick={() => setCheckedRadio(RADIO_OPTIONS.CURRENT_PROJECT)}
            >
              {COPY_MODAL_DISPLAY_CONTENT.radio.curProjText}
            </Body2Span>
          </RadioOption>
          <RadioOption>
            <StyledRadio
              data-test="another-proj-radio"
              id="another-proj-radio"
              isChecked={isAnotherProjectSelected}
              name="another-proj-radio"
              value={RADIO_OPTIONS.ANOTHER_PROJECT}
              onClick={() => setCheckedRadio(RADIO_OPTIONS.ANOTHER_PROJECT)}
            />
            <Body2Span
              as="label"
              htmlFor="another-proj-radio"
              onClick={() => setCheckedRadio(RADIO_OPTIONS.ANOTHER_PROJECT)}
            >
              {COPY_MODAL_DISPLAY_CONTENT.radio.otherProjText}
            </Body2Span>
          </RadioOption>
        </RadioContainer>
        <Spacer />

        {/* Conditionally render project select if copying to another project */}
        {/* Project to copy to select */}
        {isAnotherProjectSelected && (
          <LabelledSelect
            title={COPY_MODAL_DISPLAY_CONTENT.selects.project.title}
            id="project-select"
            data-test="project-select"
            placeholder={COPY_MODAL_DISPLAY_CONTENT.selects.project.placeholder}
            empty={COPY_MODAL_DISPLAY_CONTENT.selects.project.empty}
            options={projects}
            onSelect={setDestProjectId}
          />
        )}

        {/* Template Name Input */}
        {!isAnotherProjectSelected && (
          <TemplateNameInput
            content={COPY_MODAL_DISPLAY_CONTENT.templateNameInput}
            templateName={templateName}
            setTemplateName={setTemplateName}
            id="copy-template-name-input"
            placeholder={''}
          />
        )}

        {/* Conditionally render message type select if copying to another project and message types have been fetched */}
        {isAnotherProjectSelected && messageTypes && (
          <>
            {/* message type select */}
            <Spacer />
            <LabelledSelect
              title={COPY_MODAL_DISPLAY_CONTENT.selects.messageType.title}
              id="messageType-select"
              data-test="messageType-select"
              placeholder={COPY_MODAL_DISPLAY_CONTENT.selects.messageType.placeholder}
              empty={COPY_MODAL_DISPLAY_CONTENT.selects.messageType.empty}
              options={mapMessageTypesToOptions(messageTypes)}
              onSelect={setDestMessageTypeId}
            />
            {senderSelect}
          </>
        )}

        {isLocaleImprovementsEnabled &&
          !isEmpty(templateAndProjectLocales) &&
          isTemplateDefaultLocaleModifiable && (
            <>
              <Spacer />
              <DefaultLocaleSelectFormField
                activeTemplateLocales={templateAndProjectLocales.activeTemplateLocales}
                templateDefaultLocale={templateAndProjectLocales?.templateDefaultLocale}
                onSelect={handleDefaultLocale}
              />
            </>
          )}

        {isLocaleImprovementsEnabled &&
          !isEmpty(templateAndProjectLocales) &&
          templateAndProjectLocales.activeTemplateLocales.length > 1 && (
            <LocaleMultiSelect
              onSelect={handleCopiedLocales}
              selectedDefaultLocale={defaultLocale}
              activeTemplateLocales={templateAndProjectLocales?.activeTemplateLocales}
              templateDefaultLocale={templateAndProjectLocales?.templateDefaultLocale}
            />
          )}
      </ModalBody>

      <ModalFooter>
        <PrimaryButton
          data-test="copy-template-button"
          onClick={() => {
            dispatch(
              copyTemplate({
                currentCampaignId,
                currentFolderId,
                destEmailSender,
                destMessageTypeId,
                destProjectId,
                destProjectName,
                destSmsSenderId,
                name: templateName,
                refetch,
                srcTemplateId,
                triggeredBy,
                defaultLocaleToClone: isLocaleImprovementsEnabled ? defaultLocale.tag : '',
                secondaryLocalesToClone: isLocaleImprovementsEnabled
                  ? removeDefaultLocale(copiedLocales, defaultLocale)
                  : [],
                isCurrentProjInLocaleBeta: isLocaleImprovementsEnabled,
              }),
            );

            closeModal();
            if (onCopyTemplateClick) {
              onCopyTemplateClick();
            }
          }}
          isDisabled={shouldDisableCopyButton}
          size={BUTTON_SIZES.LARGE}
        >
          <span>{COPY_MODAL_DISPLAY_CONTENT.copyTemplateButtonText}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
